<template>
  <div>
    <v-btn
      text
      class="mb-4 px-0"
      @click="goBack"
    >
      <v-icon left>
        mdi-arrow-left
      </v-icon>
      К списку клиентов
    </v-btn>

    <v-alert
      v-if="error"
      type="error"
      dense
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <div
      v-if="loading"
      class="text-center py-8"
    >
      <v-progress-circular indeterminate></v-progress-circular>
    </div>

    <v-card
      v-else-if="card"
      outlined
      class="pa-4"
    >
      <div class="d-flex justify-space-between align-start mb-2 flex-wrap">
        <div>
          <h3 class="text-h6 mb-1">
            {{ card.name || 'Без имени' }}
          </h3>
          <v-chip
            v-if="card.client_type"
            small
            outlined
            class="mb-2"
            :color="card.is_new ? 'success' : 'primary'"
          >
            {{ card.is_new ? 'Новый клиент' : 'Постоянный клиент' }}
          </v-chip>
          <div class="text-body-2">
            {{ card.phone }}
            <span v-if="card.email"> · {{ card.email }}</span>
          </div>
          <div class="text-caption grey--text">
            customer_id: {{ card.customer_id }}
          </div>
          <div
            v-if="card.yc_first_visit_at"
            class="text-caption grey--text"
          >
            Первый визит в YClients: {{ formatDate(card.yc_first_visit_at) }}
            <span v-if="card.yc_visits_total">
              · визитов {{ card.yc_visits_total }}
            </span>
            <span v-if="card.yc_spent_total">
              · потрачено {{ formatMoney(card.yc_spent_total) }} ₽
            </span>
          </div>
        </div>
        <div class="text-right">
          <div class="text-caption grey--text">
            Выручка
          </div>
          <div class="text-h6">
            {{ formatMoney(card.revenue) }} ₽
          </div>
        </div>
      </div>

      <v-alert
        v-if="card.needs_manual_review"
        type="warning"
        dense
        text
        class="mb-3"
      >
        Требует ручного разбора склейки
      </v-alert>

      <v-row dense class="mb-3">
        <v-col cols="12" md="6">
          <div class="text-caption grey--text">
            First Touch
          </div>
          <div>
            {{ card.first_touch.source || 'direct' }}
            <span v-if="card.first_touch.campaign">
              / {{ card.first_touch.campaign }}
            </span>
          </div>
          <div
            v-if="card.first_touch.content"
            class="text-caption"
          >
            объявление: {{ card.first_touch.content }}
          </div>
          <div
            v-if="card.first_touch.yclid"
            class="text-caption"
          >
            yclid: {{ card.first_touch.yclid }}
          </div>
          <div
            v-if="card.first_touch.landing"
            class="text-caption text-truncate"
          >
            {{ card.first_touch.landing }}
          </div>
          <div class="text-caption">
            {{ card.first_touch.at || '—' }}
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="text-caption grey--text">
            Lead Touch (последний)
          </div>
          <div v-if="card.lead_touches && card.lead_touches.length">
            {{ card.lead_touches[0].source }}
            <span v-if="card.lead_touches[0].campaign">
              / {{ card.lead_touches[0].campaign }}
            </span>
          </div>
          <div
            v-else
            class="grey--text"
          >
            —
          </div>
        </v-col>
      </v-row>

      <div
        v-if="card.yclients && card.yclients.length"
        class="mb-3 text-caption"
      >
        YClients:
        <span
          v-for="(yc, i) in card.yclients"
          :key="i"
        >
          {{ yc.yc_client_id }}@{{ yc.company_id }}<span v-if="i < card.yclients.length - 1">, </span>
        </span>
      </div>

      <h4 class="subtitle-1 mb-2">
        Хронология
      </h4>
      <v-timeline
        dense
        align-top
        class="ml-n2"
      >
        <v-timeline-item
          v-for="(ev, idx) in card.timeline"
          :key="idx"
          small
          :color="timelineColor(ev.type)"
        >
          <div class="text-caption grey--text">
            {{ formatDate(ev.at) }}
          </div>
          <div class="font-weight-medium">
            {{ timelineLabel(ev) }}
          </div>
          <div
            v-if="ev.amount != null"
            class="text-body-2"
          >
            {{ formatMoney(ev.amount) }} ₽
            <span v-if="ev.title">— {{ ev.title }}</span>
          </div>
          <div
            v-else-if="ev.title"
            class="text-body-2"
          >
            {{ ev.title }}
          </div>
          <ul
            v-if="ev.details && ev.details.length"
            class="timeline-details"
          >
            <li
              v-for="(d, di) in ev.details"
              :key="di"
            >
              <span class="grey--text">{{ d.label }}:</span>
              {{ formatDetail(d) }}
            </li>
          </ul>
          <div
            v-else-if="ev.source"
            class="text-caption"
          >
            источник: {{ ev.source }}
          </div>
        </v-timeline-item>
      </v-timeline>
    </v-card>
  </div>
</template>

<script>
import Api from '@/api/api'

export default {
  name: 'AnalyticsCustomerDetail',
  data() {
    return {
      card: null,
      loading: false,
      error: null,
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    formatMoney(v) {
      return Number(v || 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 0,
      })
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDetail(d) {
      if (!d) return ''
      if (d.label === 'URL') return d.value
      if (String(d.value || '').match(/^\d{4}-\d{2}-\d{2}T/)) {
        return this.formatDate(d.value)
      }
      return d.value
    },
    timelineColor(type) {
      const map = {
        page_view: 'grey',
        booking_form_open: 'blue',
        booking_form_submit: 'indigo',
        booking_created: 'teal',
        club_visit: 'green',
        no_show: 'orange',
        purchase: 'purple',
      }
      return map[type] || 'grey'
    },
    timelineLabel(ev) {
      const map = {
        page_view: 'Визит на сайт',
        booking_form_open: 'Открыл форму',
        booking_form_submit: 'Отправил форму',
        booking_created: 'Запись в YClients',
        club_visit: 'Пришёл',
        no_show: 'Неявка',
        purchase: 'Покупка',
      }
      return map[ev.type] || ev.type
    },
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push({ name: 'AnalyticsCustomers' })
    },
    async load() {
      const id = this.$route.params.id
      if (!id) return
      this.loading = true
      this.error = null
      try {
        this.card = await Api.getAnalyticsCustomer(id)
      } catch (e) {
        this.card = null
        this.error = 'Клиент не найден'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.timeline-details {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 13px;
  line-height: 1.45;
}
</style>
